'use client';

import React from 'react';
import { cn } from '@/utilities/ui';

/**
 * Props for the GlowBorderCard component
 */
export interface GlowBorderCardProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * Content to display inside the card
     */
    children?: React.ReactNode;

    /**
     * Width of the card (CSS value)
     * @default "320px"
     */
    width?: string;

    /**
     * Height of the card (CSS value). If not provided, uses aspect-ratio.
     */
    height?: string;

    /**
     * Aspect ratio of the card (e.g., "1", "16/9", "4/3")
     * @default "1"
     */
    aspectRatio?: string;

    /**
     * Corner radius of the card
     * @default "0.75rem"
     */
    borderRadius?: string;

    /**
     * Animation duration in seconds
     * @default 4
     */
    animationDuration?: number;

    /**
     * Gradient colors array (up to 10 colors)
     */
    gradientColors?: string[];

    /**
     * Border width for the glow halo (ring thickness)
     * @default "1.25em"
     */
    borderWidth?: string;

    /**
     * Sharp visible outline width. When set, renders a crisp animated ring above the blur.
     * @default undefined
     */
    outlineWidth?: string;

    /**
     * Blur amount for the glow effect
     * @default "0.75em"
     */
    blurAmount?: string;

    /**
     * Inset distance (negative values push the border outside)
     * @default "-1em"
     */
    inset?: string;

    /**
     * Preset color themes
     */
    colorPreset?: 'nature' | 'ocean' | 'sunset' | 'aurora' | 'custom';

    /**
     * Whether animation is paused
     * @default false
     */
    paused?: boolean;

    /**
     * Classes for the inner content wrapper around children.
     */
    contentClassName?: string;
}

// Preset gradient colors (10 colors each for smooth transitions)
const colorPresets: Record<string, string[]> = {
    nature: ['#669900', '#88bb22', '#99cc33', '#aaddaa', '#ccee66', '#006699', '#228888', '#3399cc', '#55aacc', '#669900'],
    ocean: ['#006699', '#1177aa', '#2288bb', '#3399cc', '#44aadd', '#55bbee', '#66ccff', '#44bbee', '#2299cc', '#006699'],
    sunset: ['#ff6600', '#ff7711', '#ff8822', '#ff9900', '#ffaa22', '#ffbb44', '#ffcc00', '#ff9933', '#ff7722', '#ff6600'],
    aurora: ['#00ff87', '#22ffaa', '#44ffcc', '#60efff', '#88ddff', '#bb99ff', '#dd77ee', '#ff68f0', '#ff55cc', '#00ff87'],
    custom: ['#669900', '#99cc33', '#ccee66', '#006699', '#3399cc', '#990066', '#cc3399', '#ff6600', '#ff9900', '#ffcc00'],
};

function hexToRgba(hex: string, alpha: number): string {
    const normalized = hex.replace('#', '').trim();
    const full =
        normalized.length === 3
            ? normalized
                  .split('')
                  .map((character) => character + character)
                  .join('')
            : normalized;

    const r = Number.parseInt(full.slice(0, 2), 16);
    const g = Number.parseInt(full.slice(2, 4), 16);
    const b = Number.parseInt(full.slice(4, 6), 16);

    if ([r, g, b].some((channel) => Number.isNaN(channel))) {
        return `rgba(73, 233, 170, ${alpha})`;
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * GlowBorderCard - animated glowing border card.
 *
 * Soft halo uses box-shadow so Safari follows rounded corners.
 * CSS `filter: blur()` is avoided; WebKit clips that into a hard rectangle.
 * The moving outline is a conic-gradient background on the same element as radius.
 */
export const GlowBorderCard = React.forwardRef<HTMLDivElement, GlowBorderCardProps>(
    (
        {
            children,
            className,
            width = '320px',
            height,
            aspectRatio = '1',
            borderRadius = '0.75rem',
            animationDuration = 4,
            gradientColors,
            borderWidth = '1.25em',
            outlineWidth,
            blurAmount = '0.75em',
            inset = '-1em',
            colorPreset = 'custom',
            paused = false,
            contentClassName,
            style,
            ...props
        },
        ref
    ) => {
        const colors = gradientColors || colorPresets[colorPreset] || colorPresets.custom;
        const secondaryColor = colors[Math.min(3, colors.length - 1)] ?? colors[0];

        const colorVars: Record<string, string> = {};
        for (let i = 0; i < 10; i++) {
            colorVars[`--glow-color-${i + 1}`] = colors[i % colors.length];
        }

        const pausedClass = paused ? '[animation-play-state:paused]' : undefined;

        return (
            <div
                ref={ref}
                className={cn(
                    'relative flex flex-col overflow-visible bg-transparent',
                    className
                )}
                style={{
                    width: width,
                    height: height || 'auto',
                    aspectRatio: height ? 'unset' : aspectRatio,
                    borderRadius: borderRadius,
                    '--glow-animation-duration': `${animationDuration}s`,
                    '--glow-halo-width': borderWidth,
                    '--glow-ring-width': outlineWidth ?? borderWidth,
                    '--glow-blur': blurAmount,
                    '--glow-bloom-inset': inset,
                    '--glow-shadow-a': hexToRgba(colors[0], 0.7),
                    '--glow-shadow-b': hexToRgba(secondaryColor, 0.5),
                    '--glow-shadow-c': hexToRgba(colors[0], 0.28),
                    ...colorVars,
                    ...style,
                } as React.CSSProperties}
                {...props}
            >
                <div
                    aria-hidden
                    className={cn(
                        'glow-box-shadow pointer-events-none absolute z-0 bg-white/[0.001]',
                        pausedClass,
                    )}
                    style={{
                        borderRadius,
                        inset: 0,
                    }}
                />

                <div
                    className={cn(
                        'relative z-10 min-h-0 min-w-0 w-full flex-1 overflow-hidden',
                        outlineWidth && 'glow-conic-fill',
                        pausedClass,
                    )}
                    style={{
                        borderRadius,
                        padding: outlineWidth,
                    }}
                >
                    <div
                        className={cn(
                            'relative z-10 flex h-full min-h-0 w-full items-center justify-center overflow-hidden bg-cream p-4',
                            contentClassName,
                        )}
                        style={{
                            borderRadius: outlineWidth
                                ? `calc(${borderRadius} - ${outlineWidth})`
                                : borderRadius,
                        }}
                    >
                        {children}
                    </div>
                </div>
            </div>
        );
    }
);

GlowBorderCard.displayName = 'GlowBorderCard';

export default GlowBorderCard;
